import React, {useCallback, useEffect, useState} from 'react';
import {hydrationRules} from 'helpers/hydrate';
import walk from 'helpers/walk';
import utils from 'helpers/utils';
import dayjs from 'dayjs';
import {FUNCTION_PROP, ELEMENT_PROP, DATE_PROP, GENERATE_PROP} from 'helpers/styles';

// A page is done contributing when the sheet stops growing. That is the signal we actually
// care about, so the walk paces itself on it instead of on a guessed delay - slow data waits
// and fast pages do not pad.
const SETTLE = 500;
const TIMEOUT = 20000;
const POLL = 100;

// how long to wait for something to open, and for something to close
const OPENS = 3000;
const CLOSES = 400;

// Zoom out with the browser (cmd -) before starting if you want more coverage: it moves the
// media queries, so responsive branches and the labels this walk matches on all change with
// it. CSS zoom cannot do that - it widens the layout box and leaves matchMedia alone - and
// there is no API for browser zoom, so it stays a manual step.

// Table, Profile, Browser and Graphs live in this group on every entities route, and they
// already know which entity is selected - TableProvider falls back to the first row. So the
// walk clicks them rather than trying to build a path out of an id it does not have.
const TOGGLES = '[class*="EntitiesBarWrapper"] .ActionToggleButton';

// Views, Filters, Columns and Assistant open a panel over the table. TableProvider exposes
// them as toggles, so the same button closes it again - no close button to find.
const TABLE_BAR = '[class*="TableBar"]';
const PANELS = '[class*="TableBar"] .ActionButton';
const PANEL_LABELS = ['Assistant', 'Views', 'Filters', 'Columns'];

// The query bar's own Assistant button (was 'Refine assistant'), scoped so it is never the
// table toolbar's Assistant panel above of the same name. [class*] matches whichever query-bar
// wrapper the route mounts (Database / Collection) - all of them contain the button.
const QUERY_BAR = '[class*="QueryBar"]';

// What those panels render. Their own tab strips are vertical lists of filter groups, and a
// panel left open would otherwise hand them to whatever tab walk comes next.
const PANEL_CONTENT = '.TableViews, .TableFilters, .TableColumns';

// Exactly this label: a looser /^Save/ would match "Save changes" in an open edit form and
// commit it.
const SAVE_OPENERS = [/^Save search$/];

// The collection settings foldout: a gear with no label, so it is found by the icon rather
// than by text. MUI only emits data-testid outside a production build, which is where
// capturing happens anyway - in a production build this step finds nothing and is skipped.
//
// Scoped to the entities bar on purpose: the menu drawer has a gear of its own, earlier in
// the document, and clicking that one navigates to /settings instead of opening anything.
const SETTINGS = '[class*="EntitiesBarWrapper"] [data-testid="SettingsIcon"]';
const MERGE = '[class*="EntitiesBarWrapper"] [data-testid="CallMergeIcon"]';
const NOTIFY = '[class*="EntitiesBarWrapper"] [data-testid="CheckListIcon"]';

// The entity has a merge of its own, on a profile card. Scoped away from the collection's,
// which sits in the entities bar and is still on screen on the profile route.
const ENTITY_MERGE = '.EntitySystemInfoProfileCardContent [data-testid="CallMergeIcon"]';
const ENTITY_PROFILE = '.EntityProfile';

// its own class, not just a CloseIcon: a card in edit mode inside a foldout shows one too
const FOLDOUT_CLOSE = '.ProfileFoldout-toolbar-close';
const SETTINGS_WRAPPER = '.SettingsWrapper';

// The page content, so an Add on a page is found without reaching into the menu drawer.
const PAGE = '.SideMenuWrapper-content';

// Add team, Add user, Add field, Add category, Add connection, Add status... matched by shape
// rather than listed, so a new one is covered without touching this. Some open a dialog, some
// an inline card in edit mode, and some a dropdown of their own actions.
const OPENERS = [/^Add\b/, /filters$/i];
const INFO = '[data-testid="InfoOutlinedIcon"]';
const DROPDOWN = '.ContextPopper button';

// By class, not by icon: a dialog's Cancel button carries a Close icon too, so matching on
// the icon picks whichever happens to come first in the DOM.
const DIALOG = '.MuiDialog-root';
const DIALOG_CLOSE = '.DialogHeader-close';
const DIALOG_CANCEL = '.Button';

// Whatever is on top, closed by whatever it happens to offer.
const OVERLAY = '.MuiDialog-root, .ProfileFoldout';

// Marks the foldout this walk opened, so its tabs and cards are found inside it rather than
// in one that is still hanging around from an earlier step.
const MARK = 'data-hydrate';

const AVATAR = '.MenuDrawer .ActionAvatar';
const NOTIFICATIONS = '.MenuDrawer [data-testid="NotificationsIcon"]';

// Search needs a query before it renders anything: no results, no tabs, no table. React
// listens for the input event and ignores a plain value assignment, so the native setter is
// called first to get the change through.
const SEARCH = '.MenuDrawer [data-testid="SearchIcon"]';
const SEARCH_INPUT = '.SearchDialog input';
const SEARCH_TABS = '.SearchDialog-tabs .Tab';

// the comments chip in the table's timeline column - opens the timeline foldout
const TIMELINE_CELL = '.MuiTableBody-root .TimelineTableCell .Chip';

// the three cards on an entity that open a dropdown popper
const CONTEXT_CARDS = [
  '.UserContextCard-content',
  '.DealflowStatusContextCard-chip',
  '.RelevancyContextCard-button'
];

// Selecting a row brings up the selection toolbar, which nothing else in the walk renders.
const ACTIONBAR = '.Actionbar';

// Only inside the selection toolbar: Edit there opens a dialog, while Edit on a profile bar
// switches the page into edit mode and would be left open by an opener expecting a dialog.
const SELECTED_OPENERS = [/^Add\b/, /^Edit\b/, /^Download$/];

// Named, not positional: only .xlsx opens a dialog, the other entries export for real. A
// parent listed here has its menu restricted to children matching, so a reordered menu
// cannot turn into a click on the wrong one.
const CHILD_ONLY = [{parent: /^Download$/, child: /\.xlsx$/}];

// A task, a kanban card and the collection gear all render into the shared ProfileFoldout,
// so they open and close the same way.
const TASK = '.TasksDashboardCard .TaskCard';
const KANBAN_CARD = '.Kanban .EntityCard';

// The onboarding fab opens a ContextPopper holding the Onboarding, which carries its own
// close - the fab itself does not toggle it shut.
const ONBOARDING = '.CollectionOnboarding button';
const ONBOARDING_PANEL = '.Onboarding';
const ONBOARDING_CLOSE = '.Onboarding [data-testid="CloseIcon"]';

// Edit and Cancel swap places on the profile bar - each is authorised only in the state the
// other is not - so Cancel appearing is the signal that the form is up.
const PROFILE_BAR = '[class*="EntityProfileBar"] .ActionButton';

const ADD_TEAM_MEMBER = '.TeamMembersAvatarGroup [data-testid="AddIcon"]';

// the app's own dialog control, held for the length of a walk so dismiss can reach it
let hideDialog = null;

// The walk navigates away from the page that started it, so the page unmounts halfway
// through. State lives here instead, and the page re-attaches to it when it comes back.
const walkState = {
  running: false,
  steps: [],
  listeners: new Set()
};

function publish () {
  const snapshot = {running: walkState.running, steps: [...walk.steps]};
  walkState.listeners.forEach((listener) => {
    listener(snapshot);
  });
}

function fail (label, error) {
  walkState.steps.push({
    label,
    rules: 0,
    themes: {},
    path: window.location.pathname,
    error: `${error?.message ?? error}`
  });
  publish();
}

function wait (delay) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, delay);
  });
}

async function settle () {
  const start = performance.now();
  let last = hydrationRules();
  let quiet = 0;

  while (quiet < SETTLE && (performance.now() - start) < TIMEOUT) {
    await wait(POLL);
    const rules = hydrationRules();
    if (rules === last) {
      quiet += POLL;
    } else {
      quiet = 0;
      last = rules;
    }
  }

  return hydrationRules();
}

// settle() answers "has the sheet stopped growing", which is also true while a request is
// still in flight and nothing has rendered. Where the walk needs something specific to exist
// before it can go on, it waits for that instead.
async function waitFor (query, timeout = TIMEOUT) {
  const start = performance.now();

  while (query().length === 0 && (performance.now() - start) < timeout) {
    await wait(POLL);
  }

  return query().length > 0;
}

// One visit, in every theme. Switching theme re-renders the mounted tree and emits that
// theme's variants straight away, which is far cheaper than walking the whole app again.
async function visit (label, themes, setTheme) {
  const step = {label, rules: 0, themes: {}, path: window.location.pathname};
  walkState.steps.push(step);
  publish();

  for (let i = 0; i < themes.length; i += 1) {
    const before = hydrationRules();
    if (themes.length > 1) {
      setTheme(themes[i]);
    }

    const after = await settle();
    step.themes[themes[i]] = after - before;
    step.rules += after - before;
    publish();
  }

  if (themes.length > 1) {
    setTheme(themes[0]);
    await settle();
  }
}

// Every tab strip is walked the same way: read the labels once, then click each one after
// the first, re-querying each time because clicking re-renders the strip.
async function walkTabs (query, label, themes, setTheme, onTab) {
  const found = query().map((el) => {
    return el.textContent.trim();
  });

  for (let n = 1; n < found.length; n += 1) {
    const tab = query().find((el) => {
      return el.textContent.trim() === found[n];
    });

    if (tab) {
      tab.click();
      await visit(`${label} - ${found[n]}`, themes, setTheme);

      if (onTab) {
        await onTab(`${label} - ${found[n]}`);
      }
    }
  }

  return found;
}

function iconButton (selector) {
  return document.querySelector(selector)?.closest('button') ?? null;
}

function panelButton (label) {
  return [...document.querySelectorAll(PANELS)].find((el) => {
    return el.textContent.trim() === label;
  });
}

function toggles () {
  return [...document.querySelectorAll(TOGGLES)].filter(walk.isEnabled);
}

// Options / Timeline on the entity page, and any other tab group the walk lands on. The
// labels are built from descriptors rather than written out, so they are taken from the
// DOM instead of being listed here.
function tabs () {
  return [...document.querySelectorAll('.Tabs .Tab')].filter((el) => {
    return walk.isEnabled(el) && !el.closest(PANEL_CONTENT);
  });
}

function panels () {
  return [...document.querySelectorAll(PANEL_CONTENT)].filter(walk.isOnScreen);
}

function foldouts () {
  return [...document.querySelectorAll('.ProfileFoldout')];
}

function rows () {
  return walk.findRows();
}

function searchTabs () {
  return [...document.querySelectorAll(SEARCH_TABS)];
}

function dropdownItems () {
  return [...document.querySelectorAll(DROPDOWN)].filter((el) => {
    return el.textContent.trim().length > 0;
  });
}

// `patterns` may hold strings or RegExps, so a profile-bar button is found by a stable prefix
// rather than an exact label.
function profileButtons (patterns) {
  return [...document.querySelectorAll(PROFILE_BAR)].filter((el) => {
    const text = el.textContent.trim();
    return patterns.some((test) => {
      return utils.matchText(text, test);
    });
  });
}

function cancelButtons () {
  return profileButtons([/^Cancel/]);
}

// By its own class, and by which icon it currently holds: a card swaps that one button
// between Edit and Close, an open edit form is full of other Close icons (chips, field
// clears), and the red Delete carries the same class and comes first in the markup.
//
// No `.ProfileCard` ancestor - CollectionFieldProfileCard and friends are styled(Box) and
// only borrow the class for their buttons. onScreen only when choosing what to open, never
// when looking for the Close of something already open.
function cardButton (scope, icon, onScreen) {
  return [...document.querySelectorAll(`${scope} .ProfileCard-action`)].find((el) => {
    return el.querySelector(`[data-testid="${icon}Icon"]`) && (!onScreen || walk.isOnScreen(el));
  }) ?? null;
}

// A ProfileCard only shows a Close on its action while it is being edited, so this is
// unambiguous document-wide - and unlike a scoped lookup it survives the panel around it
// re-rendering, which detaches any element the step captured earlier.
function cardClose () {
  return [...document.querySelectorAll('.ProfileCard-action')].find((el) => {
    return el.querySelector('[data-testid="CloseIcon"]') && walk.isEnabled(el);
  }) ?? null;
}

function overlays () {
  return [...document.querySelectorAll(OVERLAY)].filter(walk.isOnScreen);
}

// The last one in the document is the one on top. Confirmations stack on the dialog they
// came from, and a click on anything underneath lands on the backdrop and does nothing.
function topDialog () {
  return [...document.querySelectorAll(DIALOG)].pop() ?? null;
}

function dialogClose () {
  const dialog = topDialog();
  const close = dialog?.querySelector(DIALOG_CLOSE);
  const cancel = [...(dialog?.querySelectorAll(DIALOG_CANCEL) ?? [])].find((el) => {
    return utils.matchText(el.textContent.trim(), /^Cancel/);
  });

  return (close ?? cancel)?.closest('button') ?? close ?? cancel ?? null;
}

// Tabs belonging to the dialog on top - Add source offers a few, and they are part of the
// dialog's own markup so there is nothing to wait for.
function dialogTabs () {
  const top = topDialog();
  return top ? [...top.querySelectorAll('.Tabs .Tab')] : [];
}

// Dialogs, foldouts and wizards each carry a different control, and a card left in edit mode
// is not an overlay at all - so this tries them in order.
function closeControl () {
  const top = overlays().pop();
  let control = null;

  if (top) {
    control = [
      top.querySelector(DIALOG_CLOSE)?.closest('button'),
      top.querySelector(FOLDOUT_CLOSE),
      walk.findElement(top, {label: /^Cancel/, onScreen: true}),
      walk.findElement(top, {label: /^Close/, onScreen: true})
    ].find(walk.isEnabled) ?? null;
  }

  return control ?? cardClose();
}

// Closes back down to the number of overlays there were before a step ran, rather than
// closing whatever it can find: a step that opened one thing should close one thing, and
// stacked confirmations still unwind because the count only drops when they do.
async function dismissTo (target) {
  let previous = null;

  for (let attempt = 0; attempt < 4; attempt += 1) {
    if (overlays().length <= target && !cardClose()) {
      return true;
    }

    const control = closeControl();
    if (!control) {
      return true;
    }

    // the same control still there after a click means it did not take - escalate to the
    // app's own dialog control rather than clicking it again
    if (control === previous) {
      hideDialog?.();
    } else {
      control.click();
    }

    previous = control;
    await wait(CLOSES);
  }

  return overlays().length <= target;
}

// Nothing may be left open when a route ends: an overlay is modal, so everything after it
// clicks the backdrop and quietly does nothing.
async function dismiss () {
  return dismissTo(0);
}

function ignoreAborted (e) {
  const reason = `${e.reason?.name ?? ''} ${e.reason?.message ?? e.reason ?? ''}`;
  if ((/abort/i).test(reason)) {
    e.preventDefault();
  }
}

// Poppers and info papers hang off the button that opens them, so the same button closes
// them again - no close control to find.
async function walkToggle (element, label, themes, setTheme) {
  if (element) {
    element.click();
    await visit(label, themes, setTheme);
    element.click();
    await settle();
  }
}

async function walkInfo (scope, label, themes, setTheme) {
  await walkToggle(document.querySelector(`${scope} ${INFO}`)?.closest('button'),
    `${label} - info`, themes, setTheme);
}

async function walkCard (scope, label, themes, setTheme) {
  const edit = cardButton(scope, 'Edit', true);
  if (edit) {
    edit.click();
    await waitFor(() => {
      const close = cardClose();
      return close ? [close] : [];
    }, OPENS);

    await visit(`${label} - editing`, themes, setTheme);

    // Its own button, and never dismiss(): inside a foldout the topmost overlay is the
    // foldout itself, so escalating here would close the panel this walk is running inside.
    const close = cardClose();
    if (close) {
      close.click();
      await wait(CLOSES);
    }
  }
}

function openerButtons (scope, patterns) {
  return [...document.querySelectorAll(`${scope} button`)].filter((el) => {
    const text = el.textContent.trim();
    return walk.isOnScreen(el) && patterns.some((pattern) => {
      return pattern.test(text);
    });
  });
}

// Closes what this step opened, and escalates only if the step actually left an extra
// overlay behind. Inside a foldout the topmost overlay is the foldout itself, so an
// unconditional dismiss() would close the panel the step is running inside.
async function closeOpened (scope, before) {
  const control = dialogClose() ?? cardClose();
  if (control) {
    control.click();
    await wait(CLOSES);
  }

  if (overlays().length > before) {
    await dismissTo(before);
  }
}

function opened () {
  return [dialogClose(), cardClose(), ...dropdownItems()].filter(Boolean);
}

// An opener leads to one of three things: a dialog, an inline card in edit mode, or a
// dropdown of its own actions - Add company offers Upload and Manual that way. Each child is
// reached by reopening the dropdown, because choosing one closes it.
async function walkChildren (button, scope, label, themes, setTheme, only) {
  const before = overlays().length;
  const names = dropdownItems().map((el) => {
    return el.textContent.trim();
  }).filter((name) => {
    return !only || only.test(name);
  });

  for (let n = 0; n < names.length; n += 1) {
    if (dropdownItems().length === 0) {
      button.click();
      await waitFor(dropdownItems, OPENS);
    }

    const child = dropdownItems().find((el) => {
      return el.textContent.trim() === names[n];
    });

    if (child) {
      child.click();

      const shown = await waitFor(() => {
        return [dialogClose(), ...foldouts()].filter(Boolean);
      }, OPENS);

      if (shown) {
        await visit(`${label} - ${names[n]}`, themes, setTheme);
        await walkTabs(dialogTabs, `${label} - ${names[n]}`, themes, setTheme);
        await closeOpened(scope, before);
      }
    }
  }
}

async function walkOpen (scope, label, themes, setTheme, patterns = OPENERS) {
  const names = openerButtons(scope, patterns).map((el) => {
    return el.textContent.trim();
  });

  for (let n = 0; n < names.length; n += 1) {
    const button = openerButtons(scope, patterns).find((el) => {
      return el.textContent.trim() === names[n];
    });

    if (button) {
      const before = overlays().length;
      button.click();

      if (await waitFor(opened, OPENS)) {
        await visit(`${label} - ${names[n]}`, themes, setTheme);

        if (dropdownItems().length > 0) {
          const restricted = CHILD_ONLY.find((entry) => {
            return entry.parent.test(names[n]);
          });

          await walkChildren(button, scope, `${label} - ${names[n]}`, themes, setTheme,
            restricted?.child);
        } else {
          await walkTabs(dialogTabs, `${label} - ${names[n]}`, themes, setTheme);
          await closeOpened(scope, before);
        }
      }
    }
  }
}

// Scoped to the foldout this step opened: one left over from an earlier step would otherwise
// lend its tabs and cards to this one.
async function walkFoldout (element, label, themes, setTheme, onTab) {
  if (element) {
    const before = overlays().length;
    element.click();
    await waitFor(foldouts, OPENS);

    const panel = foldouts().pop();
    const scope = `[${MARK}]`;
    panel?.setAttribute(MARK, '');

    const panelTabs = () => {
      return [...(panel?.querySelectorAll('.Tabs .Tab') ?? [])].filter(walk.isEnabled);
    };

    await visit(label, themes, setTheme);

    // tabs that only arrive with the data - if none turn up, walkTabs does nothing
    await waitFor(panelTabs, OPENS);
    await walkTabs(panelTabs, label, themes, setTheme, onTab ? (tabLabel) => {
      return onTab(tabLabel, scope);
    } : null);

    await walkCard(scope, label, themes, setTheme);

    panel?.removeAttribute(MARK);
    await dismissTo(before);
  }
}

async function walkDialog (element, label, themes, setTheme) {
  if (element) {
    const before = overlays().length;
    element.click();

    const shown = await waitFor(() => {
      const close = dialogClose();
      return close ? [close] : [];
    }, OPENS);

    if (shown) {
      await visit(label, themes, setTheme);
      await walkTabs(dialogTabs, label, themes, setTheme);
      await dismissTo(before);
    }
  }
}

async function walkOnboarding (label, themes, setTheme) {
  const fab = document.querySelector(ONBOARDING);
  if (fab) {
    fab.click();

    const shown = await waitFor(() => {
      return [...document.querySelectorAll(ONBOARDING_PANEL)];
    }, OPENS);

    if (shown) {
      await visit(`${label} - Onboarding`, themes, setTheme);

      // ActionFab toggles its own popper, so the fab closes it; the panel's own close is
      // only needed if that somehow did not take
      fab.click();
      await wait(CLOSES);

      if (document.querySelector(ONBOARDING_PANEL)) {
        const close = iconButton(ONBOARDING_CLOSE);
        if (close) {
          close.click();
          await wait(CLOSES);
        }
      }
    }
  }
}

// Selecting a row raises the selection toolbar, and its actions open dialogs of their own.
// Deselected afterwards, so the rest of the route sees the table it expects.
async function walkSelection (label, themes, setTheme) {
  const box = walk.findRowCheckbox();
  if (box) {
    box.click();
    await visit(`${label} - selected`, themes, setTheme);
    await walkOpen(ACTIONBAR, `${label} - selected`, themes, setTheme, SELECTED_OPENERS);

    const deselect = walk.findRowCheckbox();
    if (deselect) {
      deselect.click();
      await settle();
    }
  }
}

// The drawer is on every page, so its popups are worth exactly one visit.
async function walkMenu (themes, setTheme) {
  await walkToggle(document.querySelector(AVATAR), 'Account', themes, setTheme);
  await walkToggle(iconButton(NOTIFICATIONS), 'Notifications', themes, setTheme);

  const search = iconButton(SEARCH);
  if (search) {
    search.click();
    await waitFor(() => {
      const input = document.querySelector(SEARCH_INPUT);
      return input ? [input] : [];
    }, OPENS);

    const input = document.querySelector(SEARCH_INPUT);
    if (input) {
      walk.setValue(input, 'test');
      await waitFor(searchTabs, OPENS);
      await visit('Search', themes, setTheme);
      await walkTabs(searchTabs, 'Search', themes, setTheme);
    }

    await dismiss();
  }
}

async function walkContextCards (label, themes, setTheme) {
  for (let c = 0; c < CONTEXT_CARDS.length; c += 1) {
    const card = document.querySelector(CONTEXT_CARDS[c]);
    if (card && walk.isEnabled(card)) {
      await walkToggle(card, `${label} - ${CONTEXT_CARDS[c].replace(/^\./, '')}`, themes, setTheme);
    }
  }
}

async function walkEntities (route, themes, setTheme) {
  // before a row is selected: the timeline foldout is not reachable once the profile is open
  await walkToggle(document.querySelector(TIMELINE_CELL), `${route.label} - Timeline`,
    themes, setTheme);

  // while the table is still showing - the bar is gone on profile and browser
  for (let p = 0; p < PANEL_LABELS.length; p += 1) {
    const open = panelButton(PANEL_LABELS[p]);
    if (open) {
      open.click();
      await visit(`${route.label} - ${PANEL_LABELS[p]}`, themes, setTheme);

      // clicking the button again is the documented way to close it, but confirm it went:
      // a panel left open covers the bar the rest of this route needs
      for (let attempt = 0; attempt < 3 && panels().length > 0; attempt += 1) {
        const close = panelButton(PANEL_LABELS[p]);
        if (close) {
          close.click();
        }
        await wait(CLOSES);
      }

      await settle();
    }
  }

  // The query bar's Assistant: swaps the search form for the AssistantQuery conversation, the
  // only state that renders AssistantQuery / Assistant / ConversationCard / ActionCard. Matched
  // loosely (any 'Assistant' button in the query bar), and closed by any 'Back' button - the
  // label degrades from 'Back to search' to 'Back' at the xs breakpoint. Loop until no Back
  // remains, so a left-open assistant never covers the bar the rest of this route needs.
  const assistant = walk.findElement(document.querySelector(QUERY_BAR), {label: /Assistant/, onScreen: true});
  if (assistant) {
    assistant.click();
    await visit(`${route.label} - Assistant`, themes, setTheme);

    for (let attempt = 0; attempt < 3; attempt += 1) {
      const back = walk.findElement(document.querySelector(QUERY_BAR), {label: /^Back/, onScreen: true});
      if (back) {
        back.click();
      }
      await wait(CLOSES);
      if (walk.findElement(document.querySelector(QUERY_BAR), {label: /^Back/, onScreen: true}) === null) {
        break;
      }
    }

    await settle();
  }

  await walkOpen(TABLE_BAR, route.label, themes, setTheme, SAVE_OPENERS);
  await walkToggle(iconButton(NOTIFY), `${route.label} - Notifications`, themes, setTheme);
  await walkDialog(iconButton(MERGE), `${route.label} - Merge`, themes, setTheme);

  if (route.settings) {
    await walkFoldout(iconButton(SETTINGS), `${route.label} - Settings`, themes, setTheme,
      async (label, scope) => {
        await walkCard(scope, label, themes, setTheme);
        await walkOpen(scope, label, themes, setTheme);
        await walkInfo(scope, label, themes, setTheme);
      });
  }

  // read once: clicking replaces the group, and the labels stay put
  const labels = toggles().map((el) => {
    return el.textContent.trim();
  });

  for (let t = 0; t < labels.length; t += 1) {
    const button = toggles().find((el) => {
      return el.textContent.trim() === labels[t];
    });

    if (button) {
      button.click();
      await visit(`${route.label} - ${labels[t]}`, themes, setTheme);

      // whatever the page put in its tab strip - Options / Timeline on a profile
      const tabLabels = await walkTabs(tabs, `${route.label} - ${labels[t]}`, themes, setTheme);

      // back to the first, so the next step starts where it expects to
      const first = tabs().find((el) => {
        return el.textContent.trim() === tabLabels[0];
      });

      if (first && tabLabels.length > 1) {
        first.click();
        await settle();
      }

      await walkDialog(iconButton(ENTITY_MERGE), `${route.label} - ${labels[t]} - Merge`,
        themes, setTheme);

      // Before the profile-wide edit below: ProfileCard drops its own action while the whole
      // profile is editing, so afterwards there is no pencil left to click.
      await walkCard(ENTITY_PROFILE, `${route.label} - ${labels[t]}`, themes, setTheme);

      await walkContextCards(`${route.label} - ${labels[t]}`, themes, setTheme);

      // the edit form is a whole set of fields the read-only profile never renders
      const edit = profileButtons([/^Edit\b/])[0];
      if (edit) {
        edit.click();
        await waitFor(cancelButtons, OPENS);
        await visit(`${route.label} - ${labels[t]} - editing`, themes, setTheme);

        const cancel = cancelButtons()[0];
        if (cancel) {
          cancel.click();
          await settle();
        }
      }
    }
  }
}

async function walkRoute (route, themes, setTheme) {
  await visit(route.label, themes, setTheme);

  // First, so everything below runs on the page this route actually ends up on rather than
  // the list it came from - the list is walked as a route of its own anyway.
  if (route.firstRow) {
    await waitFor(rows);
    const row = walk.findRows()[0] ?? null;
    if (row) {
      row.click();
      await waitFor(toggles);
      await visit(`${route.label} - entities`, themes, setTheme);
    }
  }

  await waitFor(rows, OPENS);
  await walkSelection(route.label, themes, setTheme);
  await walkFoldout(document.querySelector(TASK), `${route.label} - Task`, themes, setTheme);
  await walkFoldout(document.querySelector(KANBAN_CARD), `${route.label} - Card`, themes, setTheme);
  await walkOnboarding(route.label, themes, setTheme);

  const addTeamMember = iconButton(ADD_TEAM_MEMBER);
  if (addTeamMember) {
    addTeamMember.click();
    await waitFor(() => {
      const close = dialogClose();
      return close ? [close] : [];
    }, OPENS);

    await visit(`${route.label} - Add team member`, themes, setTheme);
    await dismiss();
  }

  await walkOpen(PAGE, route.label, themes, setTheme);

  if (route.tabs) {
    await walkTabs(tabs, route.label, themes, setTheme, async (label) => {
      await walkCard(SETTINGS_WRAPPER, label, themes, setTheme);
      await walkOpen(SETTINGS_WRAPPER, label, themes, setTheme);
      await walkInfo(SETTINGS_WRAPPER, label, themes, setTheme);
    });
  }

  if (route.entities) {
    await walkEntities(route, themes, setTheme);
  }
}

export function useHydrationWalk () {
  const [state, setState] = useState({running: walkState.running, steps: walkState.steps});

  useEffect(() => {
    walkState.listeners.add(setState);
    return () => {
      walkState.listeners.delete(setState);
    };
  }, []);

  const start = useCallback(async (options) => {
    const {router, routes, themes, setTheme, closeDialog, home} = options;

    if (!walkState.running) {
      hideDialog = closeDialog;
      walkState.running = true;
      walkState.steps = [];
      publish();

      // Navigating away cancels whatever the page still had in flight. This quiets the
      // console entry for it, but NOT the dev overlay: preventDefault does not stop other
      // listeners, and react-error-overlay registers its own long before this one.
      window.addEventListener('unhandledrejection', ignoreAborted);

      try {
        for (let i = 0; i < routes.length; i += 1) {
          const route = routes[i];

          // A missing button or a click that lands on a backdrop should cost that route, not
          // the whole run - the failure is recorded as a step so it is visible afterwards.
          try {
            if (!await dismiss()) {
              fail(route.label, 'a dialog or foldout would not close');
            }

            router.navigate(route.path);
            await walkRoute(route, themes, setTheme);

            if (i === 0) {
              await walkMenu(themes, setTheme);
            }
          } catch (error) {
            fail(route.label, error);
          }
        }

        router.navigate(home);
      } finally {
        window.removeEventListener('unhandledrejection', ignoreAborted);
        walkState.running = false;
        publish();
      }
    }
  }, []);

  return {
    running: state.running,
    steps: state.steps,
    start
  };
}

// Both contexts below are 'weak': webpack records which files match and builds nothing for them,
// so they cost no compile time. A 'lazy' context made an async chunk per styles file and
// 'lazy-once' a second copy of every component - rebuilt on every save, which made each dev
// compile ~10s and the dev server heap ~4GB. Weak resolves from the bundle the app already
// loaded: there is no code splitting, so everything the app imports is in it, and webpack never
// evaluates a module twice, so these are the same instances 'lazy' returned. A file nothing
// imports is not in the bundle; loadedModule() skips it - it can never render, so it has no css
// to capture.
function loadedModule (context, key) {
  try {
    return context(key);
  } catch (e) {
    return null;
  }
}

// Resolves a $componentName to its *.styles modules. Built on the first generate, not at import:
// require.context walks src/components eagerly, so it is kept off the module load path. The
// context is scoped to src/components (the five category dirs - atoms / molecules / organisms /
// pages / templates). Dev-only: with process.env.NODE_ENV replaced at build, webpack drops the
// require.context dependency from the production bundle entirely.
//
// A name maps to a list, not a file. Names do overlap across the category dirs - atoms/Inputs and
// molecules/Fields both hold a DateField, atoms/Tables and organisms/Tables both a Table - and
// both stamp the same $componentName, so the manifest has already merged their propsets under
// one key and no amount of picking the right file recovers the split. Rendering every module
// registered under the name is what covers them: the wrong pairing mints one extra bounded
// variant, where keying on the file basename silently dropped a whole component.
let styleModules = null;

function resolveStyleModules () {
  if (!styleModules && process.env.NODE_ENV === 'development') {
    const context = require.context('../../components', true, /\.styles\.js$/, 'weak');
    const byName = context.keys().reduce((map, key) => {
      if (loadedModule(context, key)) {
        const name = key.split('/').pop().replace('.styles.js', '');
        map[name] = [...(map[name] ?? []), key];
      }
      return map;
    }, {});
    styleModules = {context, byName};
  }
  return styleModules;
}

// The reference set of our own components - every *.jsx default export under src/components. A
// styled(Component) whose target is in this set is one of ours, so generate renders it as a div
// instead of recursing into it (see GENERATE_PROP). Reference identity, not name: our 'Button'
// styles file wraps MUI's Button, whose displayName is also 'Button', so a name test would flag
// MUI as ours and drop its css. Resolved once into a Set from the loaded bundle (kept out of the
// production build by the NODE_ENV gate). Dev-only.
let ownComponents = null;

async function resolveOwnComponents () {
  if (!ownComponents && process.env.NODE_ENV === 'development') {
    const context = require.context('../../components', true, /\.jsx$/, 'weak');
    ownComponents = new Set(context.keys().reduce((set, key) => {
      const module = loadedModule(context, key);
      if (module?.default) {
        set.push(module.default);
      }
      return set;
    }, []));
  }
  return ownComponents;
}

// Styled targets that cannot be rendered here. The div-swap exists because our own components
// need context and data the isolated sink has no way to give them; a third-party target can be
// unrenderable for exactly the same reason - mui-tiptap's RichTextEditor boots ProseMirror with
// no extensions (the recorder drops them, an Extension is not serialisable) and throws on the
// missing 'doc' node, and recharts' series must sit inside one of its chart containers.
//
// What that costs is the wrapper's own css, not just a line in the error list: styled-components
// injects from useInsertionEffect, and an effect never runs when the subtree throws during
// render. So a target listed here renders as a div and the wrapper generates as it should.
//
// Reference identity, not name, for the reason ownComponents gives. Imported lazily so neither
// package reaches the module load path, and dev-only with the rest of generate.
let excludedComponents = null;

async function resolveExcludedComponents () {
  if (!excludedComponents && process.env.NODE_ENV === 'development') {
    const [tiptap, recharts] = await Promise.all([import('mui-tiptap'), import('recharts')]);
    excludedComponents = new Set([
      tiptap.RichTextEditor,
      tiptap.MenuBar,
      recharts.Area,
      recharts.Bar,
      recharts.Line,
      recharts.Pie
    ]);
  }
  return excludedComponents;
}

// The recorded propsets. Gated on NODE_ENV like the resolvers above, and for the same reason:
// a dynamic import keeps it out of main.js, but its guard at the call site is a runtime value, so
// webpack cannot prove it unreachable and emits the whole manifest as an async chunk that a
// production deploy carries and never fetches. Inside a branch DefinePlugin folds to false, the
// import goes with it.
async function resolveManifest () {
  let manifest = {};
  if (process.env.NODE_ENV === 'development') {
    manifest = (await import('theme/hydration.props.json')).default;
  }
  return manifest;
}

// How many components mount per animation frame - enough to fill the sheet quickly, few enough
// that a frame does not stall.
const GENERATE_BATCH = 40;

const noop = () => {};

function placeholder () {
  return React.createElement('span');
}

// Turn a recorded propset back into render props: a function marker becomes a no-op, an element
// marker and children-presence become a placeholder element.
function restore (propset) {
  return Object.keys(propset).reduce((props, key) => {
    const value = propset[key];
    if (key === 'children') {
      if (value) {
        props.children = placeholder();
      }
    } else if (value === FUNCTION_PROP) {
      props[key] = noop;
    } else if (value === ELEMENT_PROP) {
      props[key] = placeholder();
    } else if (value === DATE_PROP) {
      // now - a live dayjs, since the pickers run on AdapterDayjs; only presence matters for css.
      props[key] = dayjs();
    } else {
      props[key] = value;
    }
    return props;
  }, {});
}

// Generate state lives outside React, like the walk, so the toolbar can disable its controls
// off the same status the page is driving.
const generate = {
  status: 'idle',
  total: 0,
  rendered: 0,
  list: [],
  errors: [],
  listeners: new Set()
};

function publishGenerate () {
  const snapshot = {
    status: generate.status,
    total: generate.total,
    rendered: generate.rendered,
    list: generate.list,
    errors: generate.errors
  };
  generate.listeners.forEach((listener) => {
    listener(snapshot);
  });
}

// A mount that throws means that component's recorded props are out of date. The boundary
// reports it here so the page can list it; deduped by component so one bad variant does not
// flood, and a rendering component's props are stale across all of them anyway.
export function reportGenerateError (name, message) {
  const seen = generate.errors.some((error) => {
    return error.name === name;
  });
  if (!seen) {
    generate.errors = [...generate.errors, {name, message}];
    publishGenerate();
  }
}

// One frame-sized batch, then reschedules itself until the whole list is mounted. The page
// re-renders off each publish and mounts list.slice(0, rendered).
function renderBatch () {
  if (generate.rendered < generate.total) {
    generate.rendered = Math.min(generate.total, generate.rendered + GENERATE_BATCH);
    publishGenerate();
    window.requestAnimationFrame(renderBatch);
  } else {
    generate.status = 'done';
    publishGenerate();
  }
}

// Renders every recorded component so its css lands in the sheet, ready for Capture. The lazy
// modules are awaited outside React's render (styled(...) calls hooks in the dev build, so a
// *.styles.js must never be required during a render), then the list is mounted in frame-sized
// batches with a progress readout.
export function useHydrationGenerate () {
  const [state, setState] = useState({
    status: generate.status,
    total: generate.total,
    rendered: generate.rendered,
    list: generate.list,
    errors: generate.errors
  });

  useEffect(() => {
    generate.listeners.add(setState);
    return () => {
      generate.listeners.delete(setState);
    };
  }, []);

  const start = useCallback(async () => {
    const modules = resolveStyleModules();
    if (modules && generate.status !== 'loading' && generate.status !== 'rendering') {
      generate.status = 'loading';
      generate.total = 0;
      generate.rendered = 0;
      generate.list = [];
      generate.errors = [];
      publishGenerate();

      const manifest = await resolveManifest();

      // The predicate every rendered component receives as GENERATE_PROP: true when a styled
      // target is one of ours or on the exclude list, so the wrapper renders it as a div instead
      // of recursing into something the sink cannot render.
      const own = await resolveOwnComponents();
      const excluded = await resolveExcludedComponents();
      const isSwapped = (wrapped) => {
        return own.has(wrapped) || excluded.has(wrapped);
      };

      const entries = [];
      const names = Object.keys(manifest);
      for (let i = 0; i < names.length; i += 1) {
        const keys = modules.byName[names[i]] ?? [];
        for (let k = 0; k < keys.length; k += 1) {
          const loaded = await modules.context(keys[k]);
          const Component = loaded.default;
          manifest[names[i]].forEach((propset) => {
            const props = restore(propset);
            props[GENERATE_PROP] = isSwapped;
            entries.push({name: names[i], Component, props});
          });
        }
      }

      generate.status = 'rendering';
      generate.total = entries.length;
      generate.rendered = 0;
      generate.list = entries;
      publishGenerate();

      window.requestAnimationFrame(renderBatch);
    }
  }, []);

  return {
    status: state.status,
    total: state.total,
    rendered: state.rendered,
    list: state.list,
    errors: state.errors,
    start
  };
}
